import React, { useState, useEffect, useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import CalendarHeatmap from 'react-calendar-heatmap';
import { subDays, format, differenceInMinutes, parseISO } from 'date-fns';
import { Tooltip } from 'react-tooltip';
import './calendar-heatmap.css';
import { API_BASE_URL, apiFetch, isDemoMode } from './config';

ChartJS.register(ArcElement, ChartTooltip, Legend);

const AnalyticsDashboard = ({ onClose, username: usernameProp }) => {
  const [modeData, setModeData] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [avgSessionLength, setAvgSessionLength] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const overlayRef = useRef(null);

  // Lock background scroll when panel is open
  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    const scrollY = window.scrollY;
    const mainScrollTop = mainContent ? mainContent.scrollTop : 0;
    
    // Lock scrolling on background elements
    document.body.style.overflow = 'hidden';
    if (mainContent) mainContent.style.overflow = 'hidden';

    // Prevent wheel events from reaching background - attach to overlay
    const overlay = overlayRef.current;
    const preventScroll = (e) => {
      // Only allow scroll if inside the modal content and it can scroll
      const modalContent = overlay?.querySelector('[data-modal-content]');
      if (modalContent && modalContent.contains(e.target)) {
        const { scrollTop, scrollHeight, clientHeight } = modalContent;
        const atTop = scrollTop === 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight;
        
        // If scrolling up at top or down at bottom, prevent
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
          e.preventDefault();
        }
        // Otherwise allow normal scroll inside modal
        return;
      }
      // Block scroll outside modal content
      e.preventDefault();
    };

    overlay?.addEventListener('wheel', preventScroll, { passive: false });
    
    return () => {
      overlay?.removeEventListener('wheel', preventScroll);
      document.body.style.overflow = '';
      if (mainContent) {
        mainContent.style.overflow = '';
        mainContent.scrollTop = mainScrollTop;
      }
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const fetchLogData = async () => {
      const username = usernameProp || localStorage.getItem('username');
      if (!username) {
        setLoading(false);
        setError('Username not found. Please log in.');
        return;
      }

      if (isDemoMode()) {
        setLoading(false);
        setError('🔒 My Activity Stats — Locked in Demo Mode\n\nWith a real account this panel shows your personalized learning dashboard:\n\n  📊  Pie chart — time spent in each mode (Chat, Quiz, Code Review…)\n  📅  Activity heatmap — every day you studied over the last 6 months\n  ⏱️  Average session length\n\nExample: after a week of studying you would see a cluster of active days\non the heatmap and a pie chart showing 60% of your time in Quiz mode.\n\n✨ Sign up to start tracking your progress!');
        return;
      }

      try {
        setLoading(true);
        const response = await apiFetch(`${API_BASE_URL}/api/logs?username=${encodeURIComponent(username)}&limit=10000`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch logs: ${response.status}`);
        }

        const data = await response.json();
        const logs = data.log || [];

        if (logs.length === 0) {
          setModeData({
            labels: ['No data yet'],
            datasets: [{ data: [1], backgroundColor: ['#cccccc'], borderWidth: 1 }],
          });
          setLoading(false);
          return;
        }

        processLogData(logs);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogData();
  }, []);

  const processLogData = (logs) => {
    const modeCounts = {};
    const activityCounts = {};

    logs.forEach(log => {
      if (log.mode) {
        modeCounts[log.mode] = (modeCounts[log.mode] || 0) + 1;
      }
      if (log.created_at) {
        const date = format(parseISO(log.created_at), 'yyyy-MM-dd');
        activityCounts[date] = (activityCounts[date] || 0) + 1;
      }
    });

    setModeData({
      labels: Object.keys(modeCounts),
      datasets: [{
        label: 'Sessions by Mode',
        data: Object.values(modeCounts),
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
        ],
        borderWidth: 1,
      }],
    });

    setHeatmapData(Object.entries(activityCounts).map(([date, count]) => ({
      date: parseISO(date),
      count,
    })));

    calculateAverageSession(logs);
  };

  const calculateAverageSession = (logs) => {
    if (logs.length < 2) {
      setAvgSessionLength(0);
      return;
    }

    const sortedLogs = logs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const sessionDurations = [];
    let currentSessionStart = parseISO(sortedLogs[0].created_at);

    for (let i = 1; i < sortedLogs.length; i++) {
      const prevLogTime = parseISO(sortedLogs[i - 1].created_at);
      const currentLogTime = parseISO(sortedLogs[i].created_at);
      const diff = differenceInMinutes(currentLogTime, prevLogTime);

      if (diff > 30) {
        const duration = differenceInMinutes(prevLogTime, currentSessionStart);
        if (duration > 1) sessionDurations.push(duration);
        currentSessionStart = currentLogTime;
      }
    }

    const lastDuration = differenceInMinutes(parseISO(sortedLogs[sortedLogs.length - 1].created_at), currentSessionStart);
    if (lastDuration > 1) sessionDurations.push(lastDuration);

    if (sessionDurations.length > 0) {
      setAvgSessionLength(Math.round(sessionDurations.reduce((a, b) => a + b, 0) / sessionDurations.length));
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 10, bottom: 10 } },
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          color: '#e5e7eb',
          font: { size: 13, weight: '500' },
          padding: 20,
          boxPadding: 6
        }
      },
      title: { 
        display: true, 
        text: 'Learning Style Profile (by Mode)', 
        font: { size: 20, weight: '600' },
        color: '#86efac',
        padding: { bottom: 16, top: 0 }
      },
    },
  };

  const today = new Date();

  return (
    <div 
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '60px',
        overflow: 'hidden'
      }}>
      <div 
        data-modal-content
        style={{
        backgroundColor: '#1f2937',
        borderRadius: '16px',
        border: '2px solid rgba(210, 153, 34, 0.3)',
        padding: '24px',
        maxWidth: '1200px',
        width: '95%',
        maxHeight: '85vh',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '-6px', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, color: '#86efac', fontSize: '28px' }}>📊 Activity Stats</h2>
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>
              {usernameProp || localStorage.getItem('username') || ''}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#374151',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
            onMouseOver={(e) => { e.target.style.backgroundColor = '#f85149'; e.target.style.color = '#ffffff'; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = '#374151'; e.target.style.color = '#fff'; }}
          >
            ✕
          </button>
        </div>

        {loading && <div style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>Loading Analytics...</div>}
        
        {error && <div style={{ color: error.startsWith('🔒') ? '#fffacd' : 'red', textAlign: 'left', padding: '40px', whiteSpace: 'pre-wrap' }}>{error.startsWith('🔒') ? error : `Error: ${error}`}</div>}
        
        {!loading && !error && (
          <div>
            <Tooltip id="heatmap-tooltip" />
            <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '300px', paddingTop: '20px' }}>
                {modeData && <div style={{ position: 'relative', height: '480px' }}><Pie data={modeData} options={pieOptions} /></div>}
              </div>
              <div style={{ flex: 2, minWidth: '300px' }}>
                <h3 style={{ color: '#86efac', fontSize: '20px', fontWeight: '600', marginTop: '0px', marginBottom: '70px' }}>Activity Heatmap (Last 180 Days)</h3>
                <CalendarHeatmap
                  startDate={subDays(today, 180)}
                  endDate={today}
                  values={heatmapData}
                  classForValue={(value) => {
                    if (!value) return 'color-empty';
                    if (value.count >= 10) return 'color-github-4';
                    if (value.count >= 6) return 'color-github-3';
                    if (value.count >= 3) return 'color-github-2';
                    return 'color-github-1';
                  }}
                  tooltipDataAttrs={value => ({
                    'data-tooltip-id': 'heatmap-tooltip',
                    'data-tooltip-content': `${value?.date ? format(value.date, 'yyyy-MM-dd') : ''} has ${value?.count || 0} activities`,
                  })}
                />
                <div style={{ 
                  marginTop: '24px', 
                  textAlign: 'center', 
                  padding: '20px', 
                  backgroundColor: '#111827', 
                  borderRadius: '12px',
                  border: '1px solid #374151'
                }}>
                  <h3 style={{ color: '#86efac', fontSize: '20px', fontWeight: '600', margin: '0 0 10px 0' }}>Session Analysis</h3>
                  <p style={{ color: '#d1d5db', fontSize: '17px', margin: 0 }}>Average Session Length: <strong style={{ color: '#fbbf24', fontSize: '20px' }}>{avgSessionLength} minutes</strong></p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
